import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useGetLearningPathLessonQuizzesPaginated from "../../hooks/api/learning-path/useGetLearningPathLessonQuizzesPaginated";
import useDeleteQuiz from "../../hooks/api/quiz/useDeleteQuiz";
import { Quiz } from "../../models/Quiz";
import GenericTable from "./GenericTable";

type Props = {
  learningPathId: string;
  lessonId: string;
};

export default function QuizzesTable({ learningPathId, lessonId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: quizzes,
    isPending,
    isError,
    mutateAsync: fetchQuizzes,
    variables,
  } = useGetLearningPathLessonQuizzesPaginated(learningPathId, lessonId);
  const { mutateAsync: deleteQuiz, isPending: isDeletionPending } =
    useDeleteQuiz();

  const onDeleteConfirm = useCallback(
    (quiz: Quiz) => {
      deleteQuiz({ quizId: quiz.id! }).finally(() =>
        fetchQuizzes({ page: variables?.page ?? 0 }),
      );
    },
    [deleteQuiz, fetchQuizzes, variables?.page],
  );

  return (
    <GenericTable<Quiz>
      items={quizzes?.content ?? []}
      headers={[
        t("forms.name"),
        t("forms.description"),
        t("forms.type"),
        t("forms.schedule"),
      ]}
      rowExtractors={[
        (quiz) => quiz.title,
        (quiz) => quiz.description,
        (quiz) =>
          quiz.type === "TASK" ? t("forms.task") : t("forms.questionnaire"),
        (quiz) =>
          quiz.schedule === "AFTER" ? t("forms.after") : t("forms.before"),
      ]}
      onEditClick={(quiz) => navigate(`./quizzes/${quiz.id}`)}
      onDeleteConfirm={onDeleteConfirm}
      isDeletionPending={isDeletionPending}
      isLoading={isPending}
      isError={isError}
      refetch={fetchQuizzes}
      pagination={quizzes?.page}
    />
  );
}
